import { useEffect, useMemo, useState } from "react";
import { useCheckPersonalLoanMembershipRequirementQuery } from "@lending/hooks/queries/PersonalLoanApplication/useCheckPersonalLoanMembershipRequirementQuery";
import { useGetPersonalLoanApplicationQuery } from "@lending/hooks/queries/PersonalLoanApplication/useGetPersonalLoanApplicationQuery";
import { useListPersonalLoanOffersQuery } from "@lending/hooks/queries/PersonalLoanOffer/useListPersonalLoanOffersQuery";
import { useCreatePersonalLoanOfferSelectionQuery } from "@lending/hooks/queries/PersonalLoanOfferSelection/useCreatePersonalLoanOfferSelectionQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useApiErrorToast } from "@lending/hooks/useApiErrorToast";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useFlowNavigation } from "@lending/navigation";
import { navigateToMembershipShareStartStep } from "@lending/pages/Common/MembershipShare/hooks/navigateToMembershipShareStartStep";
import { type PersonalLoanOffer } from "@lending/types";
import { useLoanCalculator } from "./useLoanCalculator";
import { useRetriedEmptyOffers } from "./useRetriedEmptyOffers";

type CalculatedPersonalLoanOffer = PersonalLoanOffer & { monthly_payment: number };

function usePersonalLoanOffers() {
  const lendingApplicationID = useLendingApplicationID();
  const {
    data: application,
    isLoading: isApplicationLoading,
    refetch: refetchApplication,
  } = useGetPersonalLoanApplicationQuery();
  const {
    data: offersData,
    isError: isOffersError,
    isFetching: isOffersFetching,
    isLoading: isOffersLoading,
    refetch: refetchOffers,
  } = useListPersonalLoanOffersQuery();
  const { institution } = useGetInstitutionQuery();
  const { goToPath, goToStep } = useFlowNavigation();
  const handleApiError = useApiErrorToast();

  const primaryApplicantFirstName = application?.primary_applicant?.first_name ?? "";
  const coApplicantFirstName = application?.additional_applicants?.[0]?.first_name ?? "";
  const institutionName = institution.short_name ?? "";
  const requestedAmountCents = application?.requested_amount_cents ?? null;
  const allOffers = useMemo(() => offersData?.results ?? [], [offersData]);
  const { isWaitingForOffers } = useRetriedEmptyOffers({
    applicationState: application?.state,
    hasOffers: allOffers.length > 0,
    isError: isOffersError,
    isFetching: isOffersFetching,
    isLoading: isOffersLoading,
    refetch: refetchOffers,
  });

  const qualifyingPersonalLoanOffers = useMemo(() => {
    if (requestedAmountCents == null) {
      return allOffers;
    }

    return allOffers.filter((offer) => offer.offered_amount_cents >= requestedAmountCents);
  }, [allOffers, requestedAmountCents]);

  const fallbackOfferedAmountCents = useMemo(() => {
    if (
      requestedAmountCents == null ||
      allOffers.length === 0 ||
      qualifyingPersonalLoanOffers.length > 0
    ) {
      return null;
    }

    return allOffers.reduce(
      (maxAmount, offer) => Math.max(maxAmount, offer.offered_amount_cents),
      0
    );
  }, [allOffers, qualifyingPersonalLoanOffers, requestedAmountCents]);

  const hasOnlyLowerAmountOffers = fallbackOfferedAmountCents != null;

  const personalLoanOffers = useMemo(() => {
    if (fallbackOfferedAmountCents == null) {
      return qualifyingPersonalLoanOffers;
    }

    return allOffers.filter((offer) => offer.offered_amount_cents === fallbackOfferedAmountCents);
  }, [allOffers, fallbackOfferedAmountCents, qualifyingPersonalLoanOffers]);

  const sortedPersonalLoanOffers = useMemo(
    () => [...personalLoanOffers].sort((left, right) => left.term_months - right.term_months),
    [personalLoanOffers]
  );

  const maximumOfferedAmountCents = useMemo(() => {
    return personalLoanOffers.reduce(
      (maxAmount, offer) => Math.max(maxAmount, offer.offered_amount_cents),
      0
    );
  }, [personalLoanOffers]);

  const [principal, setPrincipal] = useState(0);
  const { offers } = useLoanCalculator(principal, sortedPersonalLoanOffers);

  const navigateToSubmitted = () => {
    goToStep("submitted");
  };

  const routeFromRecoveredApplicationState = async (state?: string) => {
    if (!state) {
      handleApiError(undefined);
      return;
    }

    if (state === "offer_accepted") {
      return;
    }

    if (state === "membership_share_pending" || state === "membership_selection") {
      await navigateToMembershipShareStartStep({
        applicationType: "personal-loan",
        lendingApplicationID,
        state,
        verificationRequired: false,
        goToPath,
      });
      return;
    }

    navigateToSubmitted();
  };

  const recoverFromMembershipCheckError = async () => {
    try {
      const { data: refreshedApplication } = await refetchApplication();

      await routeFromRecoveredApplicationState(refreshedApplication?.state);
    } catch {
      handleApiError(undefined);
    }
  };

  const { mutateAsync: checkMembershipRequirement, isPending: isCheckingMembership } =
    useCheckPersonalLoanMembershipRequirementQuery({
      onSuccess: async ({
        state,
        membership_share_verification_required: verificationRequired,
        membership_share_email_conflict: emailConflict,
      }) => {
        await Promise.resolve();

        if (state === "membership_share_pending" || state === "membership_selection") {
          await navigateToMembershipShareStartStep({
            applicationType: "personal-loan",
            lendingApplicationID,
            state,
            verificationRequired,
            emailConflict,
            goToPath,
          });
        } else {
          navigateToSubmitted();
        }
      },
      onError: () => {
        void recoverFromMembershipCheckError();
      },
    });

  const {
    mutateAsync,
    isPending: isAcceptingOffer,
    variables,
  } = useCreatePersonalLoanOfferSelectionQuery({
    onSuccess: async () => {
      if (application) {
        try {
          // @ts-expect-error schema incorrectly requires body for this action
          await checkMembershipRequirement({
            params: { path: { uuid: application.id } },
          });
        } catch {
          return;
        }
      }
    },
    onError: handleApiError,
  });

  useEffect(() => {
    const nextPrincipal =
      fallbackOfferedAmountCents ?? requestedAmountCents ?? maximumOfferedAmountCents;

    setPrincipal((currentPrincipal) =>
      currentPrincipal === nextPrincipal ? currentPrincipal : nextPrincipal
    );
  }, [fallbackOfferedAmountCents, requestedAmountCents, maximumOfferedAmountCents]);

  const displayAmountCents =
    fallbackOfferedAmountCents ?? requestedAmountCents ?? maximumOfferedAmountCents;
  const hasBackendOffers = allOffers.length > 0;
  const isLoading =
    isApplicationLoading || isOffersLoading || (isWaitingForOffers && !hasBackendOffers);
  const shouldShowNoOffersError = !isLoading && !hasBackendOffers;
  const shouldLogMissingOffers =
    shouldShowNoOffersError && application?.state === "offers_generated" && !isOffersError;

  async function onSubmit(offer: CalculatedPersonalLoanOffer) {
    if (!lendingApplicationID) {
      return;
    }

    try {
      if (application?.state === "offer_accepted") {
        // @ts-expect-error schema incorrectly requires body for this action
        await checkMembershipRequirement({
          params: { path: { uuid: lendingApplicationID } },
        });
        return;
      }

      await mutateAsync({
        // @ts-expect-error openapi-typescript does not respect readonly payload fields
        body: {
          application: lendingApplicationID,
          product_offer: offer.id,
          selected_amount_cents: principal,
          selected_term_months: offer.term_months,
        },
      });
    } catch {
      // onError surfaces the failure in a toast; swallow the rethrow so the
      // promise from the click handler does not become an unhandled rejection.
    }
  }

  return {
    application,
    coApplicantFirstName,
    hasOffers: hasBackendOffers,
    institution,
    institutionName,
    hasOnlyLowerAmountOffers,
    isLoading,
    isPending: isAcceptingOffer || isCheckingMembership,
    offers,
    onSubmit,
    personalLoanOffers: sortedPersonalLoanOffers,
    principal,
    primaryApplicantFirstName,
    shouldLogMissingOffers,
    shouldShowNoOffersError,
    displayAmountCents,
    variables,
  };
}

export { usePersonalLoanOffers };
export type { CalculatedPersonalLoanOffer };
